import React, { useCallback, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import Dialog from 'components/shared/Dialog/Dialog';
import Typo from 'components/shared/Typo/Typo';
import DMActionsTable from './components/DMActionsTable/DMActionsTable';
import DMActionsFiltersBar from './components/DMActionsFiltersBar/DMActionsFiltersBar';
import { DMActionsTableCols } from './components/DMActionsTable/utils';
import {
  defaultRuntimeStatuses,
  defaultTimeRange
} from '../BundleOverviewController/BundleOverviewControllerDMActions/utils';
import { useGetActionsQuery } from 'apis/cm';
import { getTimeRange } from 'components/shared/TimeSelect';
import styles from './DMActionsModal.module.css';

const DMActionsModal = ({ onClose, allDeviceIds }) => {
  const [titleId] = useState(() => v4());
  const [deviceIds, setDeviceIds] = useState(allDeviceIds);
  const [statuses, setStatuses] = useState(defaultRuntimeStatuses);
  const [dateRange, setDateRange] = useState(defaultTimeRange);
  const columns = useMemo(() => DMActionsTableCols(), []);

  const range = useMemo(() => getTimeRange(dateRange), [dateRange]);

  const isQueryEnabled = useMemo(
    () =>
      deviceIds?.length > 0 &&
      deviceIds.every((id) => typeof id === 'string' && id.trim() !== '') &&
      typeof range[0] === 'string' &&
      typeof range[1] === 'string',
    [deviceIds, range]
  );

  const deviceActionsQuery = useGetActionsQuery(
    {
      devices: deviceIds,
      runtimeStatuses: statuses,
      from: range?.[0],
      to: range?.[1]
    },
    {
      enabled: isQueryEnabled,
      placeholderData: []
    }
  );

  const { data, isFetching, isError } = deviceActionsQuery;

  const handleFiltersBarOnChange = useCallback((filterData) => {
    const { id, selection } = filterData;
    switch (id) {
      case 'device_id':
        const newFilteredDeviceIds = Object.keys(selection).filter((key) => selection[key].selected);
        if (newFilteredDeviceIds.length === 0) {
          const newDeviceIds = filterData.options;
          setDeviceIds(newDeviceIds);
        } else {
          setDeviceIds(newFilteredDeviceIds);
        }
        return;
      case 'status':
        const newFilteredStatuses = Object.keys(selection).filter((key) => selection[key].selected);
        if (newFilteredStatuses.length === 0) {
          setStatuses(defaultRuntimeStatuses);
        } else {
          setStatuses(newFilteredStatuses);
        }
        return;
      case 'date':
        setDateRange(filterData.selection);
        return;
      default:
        return;
    }
  }, []);

  const handleClose = useCallback(
    (e) => {
      onClose(e);
    },
    [onClose]
  );

  return (
    <Dialog open={true} onClose={(e) => handleClose(e)}>
      <Dialog.Container maxWidth={1308} maxHeight={666}>
        <Dialog.Title className={styles.title}>
          <Typo id={titleId} as="h3" variant="heading4">
            Device Command History
          </Typo>
        </Dialog.Title>
        <Dialog.Content>
          <DMActionsFiltersBar
            deviceIds={deviceIds}
            statuses={statuses}
            dateRange={dateRange}
            onChange={handleFiltersBarOnChange}
          />
          <DMActionsTable data={data || []} isFetching={isFetching} isError={isError} columns={columns} />
        </Dialog.Content>
        <Dialog.Close />
      </Dialog.Container>
    </Dialog>
  );
};

export default DMActionsModal;
